import { useState } from "react";
import { Link, useNavigate } from "react-router";

import {
  confirmBodyFrom,
  isBaseUpdateConflict,
  useApplyBaseUpdateMutation,
  useBaseUpdatePreviewQuery,
  type BaseUpdatePreview,
  type BaseUpdateResult,
} from "@/api/baseUpdate";
import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { classifyFreezeFailure, useFreezeCandidateMutation } from "@/api/preview";
import { useCapability } from "@/auth/SessionContext";
import { FREEZE_FAILURE_COPY } from "@/routes/ProjectPreviewDialog";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Notice } from "@/ui/Notice";

type Outcome = BaseUpdatePreview["files"][number]["outcome"];

const GROUPS = [
  {
    outcome: "kept",
    title: "Moves over as is",
    note: "The new default did not change these files. Your edits are kept.",
    tag: "kept",
    tone: "success",
  },
  {
    outcome: "replaced",
    title: "Your edits will be replaced",
    note: "The new default changed these files. After the update they are the new default's version. Your text stays in each file's history.",
    tag: "replaced",
    tone: "warning",
  },
  {
    outcome: "removed",
    title: "This file will be removed",
    note: "The new default no longer has this file. Your text stays in its history.",
    tag: "removed",
    tone: "destructive",
  },
] as const satisfies readonly {
  outcome: Outcome;
  title: string;
  note: string;
  tag: string;
  tone: "success" | "warning" | "destructive";
}[];

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

function PreviewBody({ preview }: { preview: BaseUpdatePreview }) {
  const also = [
    ...preview.libraryMoves.map(
      (choice) =>
        `${choice.name} moves from version ${choice.fromVersion} to version ${choice.toVersion}.`,
    ),
    ...preview.libraryStays.map(
      (choice) =>
        `${choice.name} stays on version ${choice.fromVersion}, the one you chose.`,
    ),
    ...(preview.currentCandidate
      ? [
          "Test version is marked out of date and can no longer be sent. Its acceptance and Lab runs stay in history.",
        ]
      : []),
  ];
  return (
    <div className="space-y-4">
      {preview.blocker === "review_open" ? (
        <Notice tone="destructive" role="alert">
          A review of this project is open. Finish or close it, then update.
        </Notice>
      ) : null}
      {GROUPS.map((group) => {
        const files = preview.files.filter(
          (file) => file.outcome === group.outcome,
        );
        return files.length === 0 ? null : (
          <section key={group.outcome} className="space-y-1">
            <h3 className="text-sm font-semibold">{group.title}</h3>
            <p className="text-xs text-muted-foreground">{group.note}</p>
            <ul className="divide-y divide-border">
              {files.map((file) => (
                <li
                  key={file.path}
                  className="flex items-center justify-between gap-2 py-1.5"
                >
                  <span dir="ltr" className="break-all font-mono text-xs">
                    {file.path}
                  </span>
                  <Badge tone={group.tone}>{group.tag}</Badge>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      {also.length > 0 ? (
        <section className="space-y-1">
          <h3 className="text-sm font-semibold">Also</h3>
          <ul className="list-disc ps-5 text-xs text-muted-foreground">
            {also.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** Reads what the update would do, and sends exactly that back on confirm. */
function BaseUpdateDialog({
  projectId,
  onClose,
  onDone,
}: {
  projectId: string;
  onClose: () => void;
  onDone: (result: BaseUpdateResult) => void;
}) {
  const preview = useBaseUpdatePreviewQuery(projectId);
  const apply = useApplyBaseUpdateMutation(projectId);
  const busy = apply.isPending;
  const data = preview.data;
  const body = data ? confirmBodyFrom(data) : null;

  async function confirm() {
    if (!body) return;
    try {
      onDone(await apply.mutateAsync(body));
    } catch {
      // Rendered below from apply.error; the dialog stays open beside it.
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent
        title="Update to the latest default"
        description={
          data?.available
            ? `From default ${data.fromSeedVersion} to ${data.toSeedVersion}.`
            : "Move this project to the default this Editor runs now."
        }
        busy={busy}
        footer={
          <>
            <span className="me-auto self-center text-xs text-muted-foreground">
              Everything updates at once. Cancel changes nothing.
            </span>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            {data?.available === false ? null : (
              <Button onClick={() => void confirm()} disabled={busy || !body}>
                Update
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-3">
          {preview.isPending ? (
            <QueryPendingState
              isPaused={preview.isPaused}
              failureReason={preview.failureReason}
              queryKey={queryKeys.baseUpdate(projectId)}
              label="Reading what the update would change"
              errorTitle="The update could not be previewed"
            />
          ) : preview.isError || !data ? (
            <QueryErrorState
              error={preview.error}
              onRetry={() => void preview.refetch()}
              title="The update could not be previewed"
              subject="The update preview"
            />
          ) : data.available ? (
            <PreviewBody preview={data} />
          ) : (
            <p className="text-sm">
              This project is already on the latest default.
            </p>
          )}
          {apply.isError ? (
            <Notice tone="destructive" role="alert">
              {isBaseUpdateConflict(apply.error)
                ? "The project changed since the update was previewed. Review the refreshed list, then update again. Nothing was changed."
                : `The update could not be applied. Nothing was changed.${apply.error instanceof ApiError ? ` ${apply.error.message}` : ""}`}
            </Notice>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** What the update did, and the way to each file's old text. */
function BaseUpdateResultView({
  projectId,
  result,
}: {
  projectId: string;
  result: BaseUpdateResult;
}) {
  const navigate = useNavigate();
  const freeze = useFreezeCandidateMutation(projectId);
  const changed = [
    ...result.replacedPaths.map((path) => ({ path, removed: false })),
    ...result.removedPaths.map((path) => ({ path, removed: true })),
  ];
  return (
    <div className="space-y-3">
      <div
        role="status"
        className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-emerald-600 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200"
      >
        <p className="min-w-0 flex-1">
          <span className="font-semibold">
            Updated to default {result.toSeedVersion}.
          </span>{" "}
          {plural(result.kept, "file", "files")} kept, {result.replaced}{" "}
          replaced, {result.removed} removed. Freeze a new test version to try
          it.
        </p>
        <Button
          variant="outline"
          disabled={freeze.isPending}
          onClick={() =>
            freeze.mutate(
              { idempotencyKey: crypto.randomUUID() },
              {
                onSuccess(created) {
                  void navigate(`/projects/${projectId}/lab`, {
                    state: { candidateId: created.candidateId },
                  });
                },
              },
            )
          }
        >
          Freeze test version
        </Button>
      </div>
      {freeze.isError ? (
        <Notice tone="destructive" role="alert">
          {FREEZE_FAILURE_COPY[classifyFreezeFailure(freeze.error).kind]}
        </Notice>
      ) : null}
      {changed.length > 0 ? (
        <section className="space-y-1">
          <h3 className="text-sm font-semibold">Replaced or removed</h3>
          <p className="text-xs text-muted-foreground">
            Your old text is in Edit pack files: open the file, Show changes,
            &quot;vs before last save&quot;.
          </p>
          <ul className="divide-y divide-border">
            {changed.map(({ path, removed }) => (
              <li
                key={path}
                className="flex items-center justify-between gap-2 py-1.5"
              >
                <span dir="ltr" className="break-all font-mono text-xs">
                  {path}
                  {removed ? (
                    <Badge tone="destructive" className="ms-2">
                      removed
                    </Badge>
                  ) : null}
                </span>
                <Button variant="outline" size="sm" asChild>
                  <Link
                    aria-label={`Open old text of ${path}`}
                    to={`/projects/${projectId}/config/files?path=${encodeURIComponent(path)}`}
                  >
                    Open old text
                  </Link>
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/**
 * The one way to move a Project onto the default this Editor mounts: the
 * notice, the confirmation and, once it is done, the result. Offered only to
 * a caller who can edit; the service enforces the same.
 */
export function BaseUpdate({
  projectId,
  newSeedVersion,
  stale,
}: {
  projectId: string;
  /** `overview.newerDefault.seedVersion`; null when the overview names none. */
  newSeedVersion: string | null;
  /** A tenant-pack Project whose preview target runs another baseline. */
  stale: boolean;
}) {
  const canUpdate = useCapability("draft.write");
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<BaseUpdateResult | null>(null);

  if (!canUpdate.allowed) return null;
  // The overview stops offering the update once it is done; the result stays.
  if (result) {
    return <BaseUpdateResultView projectId={projectId} result={result} />;
  }
  if (newSeedVersion === null && !stale) return null;
  return (
    <>
      <Notice
        action={
          <Button variant="outline" onClick={() => setOpen(true)}>
            Update to the latest default
          </Button>
        }
      >
        <span className="font-semibold">A newer default is available.</span>{" "}
        {newSeedVersion
          ? `Default ${newSeedVersion} is out.`
          : "The preview target runs a different baseline than this Project started from."}{" "}
        Update to keep working: until then you cannot freeze a new test version.
      </Notice>
      {open ? (
        <BaseUpdateDialog
          projectId={projectId}
          onClose={() => setOpen(false)}
          onDone={(done) => {
            setOpen(false);
            setResult(done);
          }}
        />
      ) : null}
    </>
  );
}
